import styled, { css, keyframes } from 'styled-components';
import Dialog from '@material-ui/core/Dialog';
import { transparentize } from 'polished';
import * as colors from '../../../../../../../config/Colors/Ligth';

export const Container = styled(Dialog)`
  .MuiDialog-paper {
    margin: 0;
    width: 540px;
    max-width: calc(100% - 32px);
    border-radius: 8px;
    overflow: hidden;
  }
`;

export const Header = styled.div`
  background-color: ${colors.formBackgroundColor};
  padding: 14px 12px 14px 24px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  h1 {
    margin: 0;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${colors.ligthFontColor};
  }
`;

export const Body = styled.div`
  background-color: ${colors.formBackgroundColor};
  padding: 8px 24px 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const pulsar = keyframes`
  0% { transform: scale(1); opacity: 0.45; }
  70% { transform: scale(1.6); opacity: 0; }
  100% { transform: scale(1.6); opacity: 0; }
`;

// Entrada com leve exagero na escala: marca o instante em que a execução termina,
// que de outra forma é só uma troca silenciosa de ícone.
const surgir = keyframes`
  0% { transform: scale(0.4); opacity: 0; }
  60% { transform: scale(1.12); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

interface AureolaProps {
  concluida: boolean;
}

/**
 * Halo em volta do ícone. Pulsa enquanto há execução e fica estático ao concluir,
 * para o estado final não parecer que ainda está trabalhando.
 */
export const Aureola = styled.div<AureolaProps>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: ${props =>
    props.concluida
      ? colors.backgroundColorSuccess
      : transparentize(0.85, colors.labelFontColor)};
  color: ${props =>
    props.concluida ? colors.colorSuccess : colors.labelFontColor};

  svg {
    font-size: 34px;
  }

  ${props =>
    props.concluida
      ? css`
          box-shadow: 0 0 0 10px ${transparentize(0.88, colors.colorSuccess)};
          animation: ${surgir} 420ms ease-out;
        `
      : css`
          &::after {
            content: '';
            position: absolute;
            inset: 0;
            border-radius: 50%;
            border: 2px solid ${colors.labelFontColor};
            animation: ${pulsar} 1.8s ease-out infinite;
          }
        `}
`;

interface TituloProps {
  concluida: boolean;
}

export const Titulo = styled.h2<TituloProps>`
  margin: 0;
  font-size: ${props => (props.concluida ? '20px' : '18px')};
  font-weight: 700;
  color: ${props =>
    props.concluida ? colors.colorSuccess : colors.titleFontColor};
  text-align: center;
`;

export const Detalhe = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: ${colors.ligthFontColor};
  text-align: center;
  max-width: 400px;
`;

export const Passos = styled.ol`
  list-style: none;
  margin: 4px 0 0;
  padding: 0;
  display: flex;
  align-items: flex-start;
  width: 100%;
`;

interface PassoProps {
  estado: 'pendente' | 'atual' | 'concluido';
  primeiro: boolean;
}

export const Passo = styled.li<PassoProps>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  position: relative;
  font-size: 11px;
  font-weight: ${props => (props.estado === 'atual' ? 700 : 500)};
  text-align: center;

  color: ${props => {
    if (props.estado === 'pendente')
      return transparentize(0.4, colors.ligthFontColor);

    return props.estado === 'atual'
      ? colors.labelFontColor
      : colors.colorSuccess;
  }};

  /* Trilha ligando ao passo anterior, atrás do marcador. */
  ${props =>
    !props.primeiro &&
    css`
      &::before {
        content: '';
        position: absolute;
        top: 5px;
        right: 50%;
        left: -50%;
        height: 2px;
        background: ${props.estado === 'pendente'
          ? '#e4e4e4'
          : colors.colorSuccess};
      }
    `}

  span {
    position: relative;
    z-index: 1;
  }
`;

export const Marcador = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: currentColor;
  box-shadow: 0 0 0 3px ${colors.formBackgroundColor};
`;

export const Resumo = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-top: 1px solid #eee;
  padding-top: 20px;
`;

export const Linha = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  small {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${colors.ligthFontColor};
  }

  strong {
    font-size: 13px;
    font-weight: 600;
    color: ${colors.darkFontColor};
  }
`;

export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const Chip = styled.span`
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  color: ${colors.colorLigthButton};
  background: ${transparentize(0.75, colors.backgroundColorLigthButton)};
  border: 1px solid ${transparentize(0.5, colors.backgroundColorLigthButton)};
`;
